import { createSlice, PayloadAction, createAction } from '@reduxjs/toolkit';
import { DailySchedule, TodayScheduleData, DailySubtopicMapping } from '@/services/learningProgress';

// Async actions for saga
export const fetchTodaySchedule = createAction('dailySchedule/fetchTodaySchedule');
export const fetchTodayScheduleSuccess = createAction<TodayScheduleData>('dailySchedule/fetchTodayScheduleSuccess');
export const fetchTodayScheduleFailure = createAction<string>('dailySchedule/fetchTodayScheduleFailure');

export const fetchAvailableSchedules = createAction('dailySchedule/fetchAvailableSchedules');
export const fetchAvailableSchedulesSuccess = createAction<DailySchedule[]>(
    'dailySchedule/fetchAvailableSchedulesSuccess',
);
export const fetchAvailableSchedulesFailure = createAction<string>('dailySchedule/fetchAvailableSchedulesFailure');

export const fetchScheduleWithSubtopics = createAction<{ scheduleId: string }>(
    'dailySchedule/fetchScheduleWithSubtopics',
);
export const fetchScheduleWithSubtopicsSuccess = createAction<TodayScheduleData>(
    'dailySchedule/fetchScheduleWithSubtopicsSuccess',
);
export const fetchScheduleWithSubtopicsFailure = createAction<string>(
    'dailySchedule/fetchScheduleWithSubtopicsFailure',
);

export interface DailyScheduleState {
    todaySchedule: TodayScheduleData | null;
    availableSchedules: DailySchedule[];
    selectedSchedule: TodayScheduleData | null;
    subtopicMappings: DailySubtopicMapping[];
    isLoading: boolean;
    isLoadingSubtopics: boolean;
    error: string | null;
    lastUpdated: string | null;
}

const initialState: DailyScheduleState = {
    todaySchedule: null,
    availableSchedules: [],
    selectedSchedule: null,
    subtopicMappings: [],
    isLoading: false,
    isLoadingSubtopics: false,
    error: null,
    lastUpdated: null,
};

const dailyScheduleSlice = createSlice({
    name: 'dailySchedule',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        resetState: () => initialState,
        setSelectedSchedule: (state, action: PayloadAction<TodayScheduleData>) => {
            state.selectedSchedule = action.payload;
            state.subtopicMappings = action.payload.subtopic_mappings || [];
        },
        updateSubtopicMapping: (state, action: PayloadAction<DailySubtopicMapping>) => {
            const index = state.subtopicMappings.findIndex((mapping) => mapping.id === action.payload.id);
            if (index >= 0) {
                state.subtopicMappings[index] = action.payload;
            }

            // Update in selected schedule if it exists
            if (state.selectedSchedule && state.selectedSchedule.subtopic_mappings) {
                const selectedIndex = state.selectedSchedule.subtopic_mappings.findIndex(
                    (mapping) => mapping.id === action.payload.id,
                );
                if (selectedIndex >= 0) {
                    state.selectedSchedule.subtopic_mappings[selectedIndex] = action.payload;
                }
            }

            // Update in today schedule if it exists
            if (state.todaySchedule && state.todaySchedule.subtopic_mappings) {
                const todayIndex = state.todaySchedule.subtopic_mappings.findIndex(
                    (mapping) => mapping.id === action.payload.id,
                );
                if (todayIndex >= 0) {
                    state.todaySchedule.subtopic_mappings[todayIndex] = action.payload;
                }
            }
        },
        markScheduleCompleted: (state, action: PayloadAction<string>) => {
            // Update available schedules
            const scheduleIndex = state.availableSchedules.findIndex((schedule) => schedule.id === action.payload);
            if (scheduleIndex >= 0) {
                state.availableSchedules[scheduleIndex].is_completed = true;
                state.availableSchedules[scheduleIndex].completion_percentage = 100;
            }

            // Update today schedule if it matches
            if (state.todaySchedule && state.todaySchedule.daily_schedule.id === action.payload) {
                state.todaySchedule.daily_schedule.is_completed = true;
                state.todaySchedule.daily_schedule.completion_percentage = 100;
            }
        },
    },
    extraReducers: (builder) => {
        // Fetch Today Schedule
        builder
            .addCase(fetchTodaySchedule, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchTodayScheduleSuccess, (state, action) => {
                state.isLoading = false;
                console.log('fetchTodayScheduleSuccess', action.payload);
                state.todaySchedule = action.payload;
                state.lastUpdated = new Date().toISOString();
                state.error = null;
            })
            .addCase(fetchTodayScheduleFailure, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });

        // Fetch Available Schedules
        builder
            .addCase(fetchAvailableSchedules, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchAvailableSchedulesSuccess, (state, action) => {
                state.isLoading = false;
                state.availableSchedules = action.payload;
                state.lastUpdated = new Date().toISOString();
                state.error = null;
            })
            .addCase(fetchAvailableSchedulesFailure, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });

        // Fetch Schedule With Subtopics
        builder
            .addCase(fetchScheduleWithSubtopics, (state) => {
                state.isLoadingSubtopics = true;
                state.error = null;
            })
            .addCase(fetchScheduleWithSubtopicsSuccess, (state, action) => {
                state.isLoadingSubtopics = false;
                state.selectedSchedule = action.payload;
                state.subtopicMappings = action.payload.subtopic_mappings || [];
                state.lastUpdated = new Date().toISOString();
                state.error = null;
            })
            .addCase(fetchScheduleWithSubtopicsFailure, (state, action) => {
                state.isLoadingSubtopics = false;
                state.error = action.payload;
            });
    },
});

export const { clearError, resetState, setSelectedSchedule, updateSubtopicMapping, markScheduleCompleted } =
    dailyScheduleSlice.actions;

export default dailyScheduleSlice.reducer;
